import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, ShieldCheck, Clock } from "lucide-react";
import content from "@/data/content.json";
import personal from "@/data/personal.json";
import { AccentH2, Container, Label, Reveal, Section } from "./Primitives";
import { FlowLines } from "./FlowLines";

const { meta } = content;
const { formular } = personal;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full rounded-[10px] border border-[#d4d8d5] bg-[#f8f9f8] px-4 py-3.5 text-[16px] text-[#1e2532] outline-none placeholder:text-[#9aa1ad] focus:border-[#0f9d6e] focus:bg-white";
const labelCls = "mb-2 block text-[13.5px] font-semibold text-[#3c4356]";

const rolleFromQuery = () => {
  if (typeof window === "undefined") return "";
  const slug = new URLSearchParams(window.location.search).get("rolle");
  if (!slug) return "";
  const entry = personal.rollen[slug];
  return entry ? entry.navLabel : "";
};

export const StelleBesetzenForm = ({ defaultPosition = "" }) => {
  const [form, setForm] = useState(() => ({
    name: "",
    company: "",
    email: "",
    phone: "",
    position: rolleFromQuery() || defaultPosition || "",
    anzahl: "1",
    region: "",
    gehalt: "",
    message: "",
    consent: false,
  }));
  const [sending, setSending] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email || !form.phone || !form.position || !form.consent) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus und bestätigen Sie die Einwilligung.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/stelle`, form);
      window.location.href = "/personalvermittlung/danke/";
    } catch (err) {
      toast.error("Die Anfrage konnte nicht gesendet werden. Bitte rufen Sie uns an.");
      setSending(false);
    }
  };

  return (
    <Section
      id="formular"
      testId="section-stelle-besetzen"
      className="has-lines overflow-hidden bg-[#1e2532] text-white on-dark"
    >
      <FlowLines />
      <Container>
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.78fr_1.22fr] lg:gap-14">
          <Reveal>
            <Label>{formular.label}</Label>
            <AccentH2 className="mt-6 font-bold">{formular.h2}</AccentH2>
            <p className="mt-6 max-w-[420px] text-[16.5px] leading-[1.76] text-white/65">{formular.intro}</p>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {["Rein erfolgsbasiert", "Antwort in 24 Stunden", "Vertraulich"].map((b, i) => (
                <span
                  key={b}
                  className="inline-flex items-center gap-2 rounded-full border border-white/14 bg-white/[0.05] px-3.5 py-2 text-[13.5px] text-white/80"
                >
                  {i === 1 ? (
                    <Clock size={14} className="text-[#3ecf9b]" />
                  ) : (
                    <ShieldCheck size={14} className="text-[#3ecf9b]" />
                  )}
                  {b}
                </span>
              ))}
            </div>
            <a
              href={meta.phoneHref}
              data-testid="stelle-phone"
              className="mt-8 inline-flex items-center gap-3.5 text-[16px] text-white/85"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/14 bg-white/[0.05]">
                <Phone size={16} className="text-[#3ecf9b]" />
              </span>
              <span className="link-underline">{meta.phone}</span>
            </a>
          </Reveal>

          <Reveal>
            <form
              onSubmit={submit}
              data-testid="stelle-form"
              className="rounded-[20px] border border-[#dfe2e0] bg-white p-7 text-[#1e2532] md:p-10"
              noValidate
            >
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <label className="block">
                  <span className={labelCls}>Name *</span>
                  <input data-testid="stelle-name" className={field} value={form.name} onChange={(e) => set("name", e.target.value)} autoComplete="name" />
                </label>
                <label className="block">
                  <span className={labelCls}>Unternehmen *</span>
                  <input data-testid="stelle-company" className={field} value={form.company} onChange={(e) => set("company", e.target.value)} autoComplete="organization" />
                </label>
                <label className="block">
                  <span className={labelCls}>E-Mail *</span>
                  <input data-testid="stelle-email" type="email" className={field} value={form.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" />
                </label>
                <label className="block">
                  <span className={labelCls}>Rufnummer *</span>
                  <input data-testid="stelle-phone-input" type="tel" className={field} value={form.phone} onChange={(e) => set("phone", e.target.value)} autoComplete="tel" />
                </label>
                <label className="block">
                  <span className={labelCls}>Position *</span>
                  <select data-testid="stelle-position" className={field} value={form.position} onChange={(e) => set("position", e.target.value)}>
                    <option value="">Bitte wählen</option>
                    {formular.positionen.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className={labelCls}>Anzahl Stellen</span>
                  <input data-testid="stelle-anzahl" className={field} value={form.anzahl} onChange={(e) => set("anzahl", e.target.value)} inputMode="numeric" />
                </label>
                <label className="block">
                  <span className={labelCls}>Einsatzort / Region</span>
                  <input data-testid="stelle-region" className={field} value={form.region} onChange={(e) => set("region", e.target.value)} placeholder="z. B. Raum Köln" />
                </label>
                <label className="block">
                  <span className={labelCls}>Gehaltsrahmen (optional)</span>
                  <input data-testid="stelle-gehalt" className={field} value={form.gehalt} onChange={(e) => set("gehalt", e.target.value)} placeholder="z. B. 60.000–75.000 €" />
                </label>
              </div>

              <label className="mt-6 block">
                <span className={labelCls}>Nachricht</span>
                <textarea data-testid="stelle-message" rows={4} className={field} value={form.message} onChange={(e) => set("message", e.target.value)} />
              </label>

              <label className="mt-8 flex items-start gap-3 text-[14.5px] leading-[1.6] text-[#565f70]">
                <input
                  data-testid="stelle-consent"
                  type="checkbox"
                  className="mt-1 h-[18px] w-[18px] shrink-0"
                  checked={form.consent}
                  onChange={(e) => set("consent", e.target.checked)}
                />
                <span>
                  Ich habe die{" "}
                  <a href="/datenschutzerklaerung/" className="link-underline font-semibold text-[#1e2532]">
                    Datenschutzerklärung
                  </a>{" "}
                  gelesen und willige in die Verarbeitung meiner Angaben zur Bearbeitung meiner Anfrage ein. *
                </span>
              </label>

              <button
                type="submit"
                data-testid="stelle-submit"
                disabled={sending}
                className="btn-solid mt-9 inline-flex w-full items-center justify-center px-8 py-[17px] text-[14px] font-bold uppercase tracking-[0.06em] disabled:opacity-60"
              >
                <span>{sending ? "Wird gesendet …" : "Stelle besprechen"}</span>
              </button>
              <p className="mt-4 text-center text-[13px] text-[#8a90a0]">
                Antwort innerhalb von 24 Stunden · Kostenlos und unverbindlich
              </p>
            </form>
          </Reveal>
        </div>
      </Container>
    </Section>
  );
};
